if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mediaIndex = 0;
        this.imageZoom = 1;
        this.mediaTouchStart = null;
        this.mediaTouchAxis = null;
        this.pinchStartDistance = null;
        this.pinchStartZoom = 1;
        this.suppressImageClickUntil = 0;
        this.setupMediaControls();
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      setupMediaControls() {
        const content = this.querySelector('.product-media-modal__content');
        const toolbar = this.querySelector('.product-media-modal__toolbar');

        content?.addEventListener('pointerup', (event) => event.stopPropagation());
        toolbar?.addEventListener('pointerup', (event) => event.stopPropagation());
        this.querySelectorAll('.product-media-modal__nav').forEach((button) => {
          button.addEventListener('pointerup', (event) => event.stopPropagation());
        });

        this.querySelector('[data-media-modal-prev]')?.addEventListener('click', () => this.stepMedia(-1));
        this.querySelector('[data-media-modal-next]')?.addEventListener('click', () => this.stepMedia(1));
        this.querySelector('[data-media-zoom-out]')?.addEventListener('click', () => this.setZoom(this.imageZoom - 0.25));
        this.querySelector('[data-media-zoom-in]')?.addEventListener('click', () => this.setZoom(this.imageZoom + 0.25));
        this.querySelector('[data-media-zoom-reset]')?.addEventListener('click', () => this.setZoom(1));

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.stepMedia(-1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.stepMedia(1);
          }
        });

        content?.addEventListener('click', (event) => {
          const image = event.target.closest('img[data-media-id]');
          if (!image || Date.now() < this.suppressImageClickUntil) return;
          event.preventDefault();
          this.setZoom(this.imageZoom > 1 ? 1 : 2);
        });

        content?.addEventListener(
          'wheel',
          (event) => {
            if (!event.target.closest('img[data-media-id]')) return;
            event.preventDefault();
            this.setZoom(this.imageZoom + (event.deltaY < 0 ? 0.25 : -0.25));
          },
          { passive: false }
        );

        content?.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length === 1) {
              const touch = event.touches[0];
              this.mediaTouchStart = { x: touch.clientX, y: touch.clientY };
              this.mediaTouchAxis = null;
              this.pinchStartDistance = null;
              this.prepareActiveImageForDrag();
            } else if (event.touches.length === 2) {
              this.mediaTouchStart = null;
              this.mediaTouchAxis = null;
              this.pinchStartDistance = this.touchDistance(event.touches);
              this.pinchStartZoom = this.imageZoom;
            }
          },
          { passive: true }
        );

        content?.addEventListener(
          'touchmove',
          (event) => {
            if (event.touches.length === 2 && this.pinchStartDistance) {
              event.preventDefault();
              const distance = this.touchDistance(event.touches);
              this.setZoom(this.pinchStartZoom * (distance / this.pinchStartDistance));
              this.suppressImageClickUntil = Date.now() + 400;
              return;
            }
            if (event.touches.length !== 1 || !this.mediaTouchStart || this.imageZoom > 1.05) return;

            const touch = event.touches[0];
            const deltaX = touch.clientX - this.mediaTouchStart.x;
            const deltaY = touch.clientY - this.mediaTouchStart.y;
            if (!this.mediaTouchAxis && Math.hypot(deltaX, deltaY) > 8) {
              this.mediaTouchAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
            }
            if (!this.mediaTouchAxis) return;

            event.preventDefault();
            this.dragActiveImage(deltaX, deltaY);
            this.suppressImageClickUntil = Date.now() + 400;
          },
          { passive: false }
        );

        content?.addEventListener(
          'touchend',
          (event) => {
            if (this.pinchStartDistance) {
              if (event.touches.length < 2) this.pinchStartDistance = null;
              this.mediaTouchStart = null;
              this.mediaTouchAxis = null;
              return;
            }
            if (event.changedTouches.length !== 1 || this.mediaTouchStart == null) return;
            if (this.imageZoom > 1.05) {
              this.mediaTouchStart = null;
              this.mediaTouchAxis = null;
              return;
            }
            const deltaX = event.changedTouches[0].clientX - this.mediaTouchStart.x;
            const deltaY = event.changedTouches[0].clientY - this.mediaTouchStart.y;
            const axis = this.mediaTouchAxis;
            this.mediaTouchStart = null;
            this.mediaTouchAxis = null;
            this.finishActiveImageDrag(axis, deltaX, deltaY);
          },
          { passive: true }
        );

        content?.addEventListener('touchcancel', () => {
          this.mediaTouchStart = null;
          this.mediaTouchAxis = null;
          this.resetActiveImagePosition();
        });
      }

      touchDistance(touches) {
        return Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
      }

      normalizeMediaId(value) {
        return String(value || '').split('-').pop();
      }

      getModalMediaItems() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return [];

        return [...content.children]
          .map((element) => {
            const mediaElement = element.matches('[data-media-id]') ? element : element.querySelector('[data-media-id]');
            return mediaElement
              ? { element, mediaElement, id: this.normalizeMediaId(mediaElement.dataset.mediaId) }
              : null;
          })
          .filter(Boolean);
      }

      getVisibleGalleryMediaIds() {
        const gallery = this.openedBy?.closest('media-gallery');
        if (!gallery) return [];

        return [...gallery.querySelectorAll('[id^="Slider-Gallery-"] > li[data-media-id]')]
          .filter(
            (element) =>
              !element.hidden &&
              !element.classList.contains('hidden') &&
              window.getComputedStyle(element).display !== 'none'
          )
          .map((element) => this.normalizeMediaId(element.dataset.mediaId));
      }

      getActiveVariantMediaItems() {
        const allItems = this.getModalMediaItems();
        const visibleIds = new Set(this.getVisibleGalleryMediaIds());
        if (!visibleIds.size) return allItems;

        const filteredItems = allItems.filter((item) => visibleIds.has(item.id));
        return filteredItems.length ? filteredItems : allItems;
      }

      showActiveMedia() {
        const items = this.getActiveVariantMediaItems();
        const openerId = this.normalizeMediaId(this.openedBy?.getAttribute('data-media-id'));
        const requestedIndex = items.findIndex((item) => item.id === openerId);
        this.mediaIndex = requestedIndex >= 0 ? requestedIndex : 0;
        this.renderMedia();
      }

      stepMedia(direction) {
        const items = this.getActiveVariantMediaItems();
        if (!items.length) return;

        const nextIndex = Math.min(items.length - 1, Math.max(0, this.mediaIndex + direction));
        if (nextIndex === this.mediaIndex) {
          this.updateControls(items.length);
          return;
        }
        this.mediaIndex = nextIndex;
        this.renderMedia();
      }

      renderMedia() {
        const allItems = this.getModalMediaItems();
        const allowedItems = this.getActiveVariantMediaItems();
        const activeItem = allowedItems[this.mediaIndex] || allowedItems[0];
        const activeElement = activeItem?.element;
        this.mediaIndex = Math.max(0, allowedItems.indexOf(activeItem));

        allItems.forEach((item) => {
          const isActive = item.element === activeElement;
          item.element.hidden = !isActive;
          item.element.classList.toggle('active', isActive);
          item.element.classList.toggle('is-modal-active', isActive);
          item.element.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          this.getItemImages(item).forEach((image) => {
            image.style.transform = 'scale(1)';
            image.style.transformOrigin = 'center center';
            image.style.transition = '';
            image.style.opacity = '';
          });
        });

        this.imageZoom = 1;
        this.updateControls(allowedItems.length);
        this.querySelector('.product-media-modal__content')?.scrollTo({ top: 0, left: 0 });

        if (activeItem?.mediaElement.nodeName === 'DEFERRED-MEDIA') activeItem.mediaElement.loadContent?.();
      }

      getActiveImage() {
        const item = this.getActiveVariantMediaItems()[this.mediaIndex];
        if (!item) return null;
        return item.element.matches('img[data-media-id]')
          ? item.element
          : item.element.querySelector('img[data-media-id]');
      }

      getItemImages(item) {
        if (item.element.matches('img')) return [item.element];
        return [...item.element.querySelectorAll('img')];
      }

      prepareActiveImageForDrag() {
        const image = this.getActiveImage();
        if (!image || this.imageZoom > 1.05) return;
        image.style.transition = 'none';
        image.style.opacity = '1';
      }

      dragActiveImage(deltaX, deltaY) {
        const image = this.getActiveImage();
        if (!image) return;
        const translateX = this.mediaTouchAxis === 'horizontal' ? deltaX : 0;
        const translateY = this.mediaTouchAxis === 'vertical' ? Math.max(0, deltaY) : 0;
        image.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(1)`;
        image.style.opacity = this.mediaTouchAxis === 'vertical'
          ? String(Math.max(0.35, 1 - translateY / Math.max(window.innerHeight * 0.7, 1)))
          : '1';
      }

      finishActiveImageDrag(axis, deltaX, deltaY) {
        const image = this.getActiveImage();
        if (!image) return;
        this.suppressImageClickUntil = Date.now() + 400;

        if (axis === 'vertical' && deltaY > Math.min(140, window.innerHeight * 0.16)) {
          image.style.transition = 'transform 180ms ease-out, opacity 180ms ease-out';
          image.style.transform = 'translate3d(0, 110vh, 0) scale(1)';
          image.style.opacity = '0';
          window.setTimeout(() => this.hide(), 180);
          return;
        }

        if (axis === 'horizontal' && Math.abs(deltaX) > 45) {
          const items = this.getActiveVariantMediaItems();
          const direction = deltaX > 0 ? -1 : 1;
          const canNavigate = direction < 0 ? this.mediaIndex > 0 : this.mediaIndex < items.length - 1;
          if (canNavigate) {
            image.style.transition = 'transform 150ms ease-out, opacity 150ms ease-out';
            image.style.transform = `translate3d(${deltaX > 0 ? '110vw' : '-110vw'}, 0, 0) scale(1)`;
            image.style.opacity = '0';
            window.setTimeout(() => this.stepMedia(direction), 150);
            return;
          }
        }

        this.resetActiveImagePosition();
      }

      resetActiveImagePosition() {
        const image = this.getActiveImage();
        if (!image) return;
        image.style.transition = 'transform 180ms ease-out, opacity 180ms ease-out';
        image.style.transform = 'translate3d(0, 0, 0) scale(1)';
        image.style.opacity = '1';
      }

      setZoom(zoom) {
        const image = this.getActiveImage();
        if (!image) return;

        this.imageZoom = Math.min(4, Math.max(1, Math.round(zoom * 100) / 100));
        image.style.transform = `scale(${this.imageZoom})`;
        image.style.transformOrigin = 'center center';
        image.classList.toggle('is-zoomed', this.imageZoom > 1);
        this.querySelector('.product-media-modal__content')?.toggleAttribute('data-zoomed', this.imageZoom > 1);
        this.updateZoomControls();
      }

      resetZoom() {
        this.getModalMediaItems().forEach((item) => {
          this.getItemImages(item).forEach((image) => {
            image.style.transform = 'scale(1)';
            image.classList.remove('is-zoomed');
          });
        });
        this.imageZoom = 1;
        this.querySelector('.product-media-modal__content')?.removeAttribute('data-zoomed');
        this.updateZoomControls();
      }

      updateControls(itemCount) {
        const previous = this.querySelector('[data-media-modal-prev]');
        const next = this.querySelector('[data-media-modal-next]');
        const counter = this.querySelector('[data-media-modal-counter]');
        const hasSeveralImages = itemCount > 1;

        if (previous) {
          previous.hidden = !hasSeveralImages;
          previous.disabled = this.mediaIndex <= 0;
        }
        if (next) {
          next.hidden = !hasSeveralImages;
          next.disabled = this.mediaIndex >= itemCount - 1;
        }
        if (counter) {
          counter.hidden = !hasSeveralImages;
          counter.textContent = itemCount ? `${this.mediaIndex + 1} / ${itemCount}` : '';
        }
        this.updateZoomControls();
      }

      updateZoomControls() {
        const status = this.querySelector('[data-media-zoom-status]');
        const zoomOut = this.querySelector('[data-media-zoom-out]');
        const zoomIn = this.querySelector('[data-media-zoom-in]');
        if (status) status.textContent = `${Math.round(this.imageZoom * 100)} %`;
        if (zoomOut) zoomOut.disabled = this.imageZoom <= 1;
        if (zoomIn) zoomIn.disabled = this.imageZoom >= 4;
      }
    }
  );
}
